import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import ConfirmModal from '../common/ConfirmModal';
import { useConfirmModal } from '../../hooks/useConfirmModal';
import {
  getSubscriptions,
  getSubscriptionStats,
  getPlans,
  grantSubscription,
  grantSubscriptionToAll,
  extendSubscription,
  cancelSubscription,
  Subscription,
  SubscriptionPlan,
  SubscriptionStats,
} from '../../services/adminSubscriptionService';
import axios from 'axios';
import { API_URL } from '../../constants/api';

// Styles matching OperationManagement
const Container = styled.div`
  margin-bottom: 20px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const PageTitle = styled.h2`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.75rem;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin: 0;
  font-weight: 700;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 15px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StatCard = styled.div`
  background: rgba(0, 30, 60, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  padding: 15px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #00d4ff;
  font-family: 'Courier New', monospace;
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  margin-top: 5px;
  font-family: 'Courier New', monospace;
`;

const ActionsBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'success' | 'warning' }>`
  background: ${props => {
    switch (props.$variant) {
      case 'success': return 'rgba(0, 170, 0, 0.3)';
      case 'warning': return 'rgba(255, 165, 0, 0.3)';
      default: return 'rgba(0, 30, 60, 0.8)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$variant) {
      case 'success': return 'rgba(0, 255, 136, 0.5)';
      case 'warning': return 'rgba(255, 165, 0, 0.5)';
      default: return 'rgba(0, 212, 255, 0.4)';
    }
  }};
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: 700;
  padding: 10px 18px;
  cursor: pointer;
  border-radius: 4px;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => {
      switch (props.$variant) {
        case 'success': return 'rgba(0, 255, 136, 0.2)';
        case 'warning': return 'rgba(255, 165, 0, 0.2)';
        default: return 'rgba(0, 212, 255, 0.2)';
      }
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
  align-items: center;
`;

const FilterLabel = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
`;

const Select = styled.select`
  padding: 10px 15px;
  background: rgba(0, 30, 60, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #ffffff;
  font-size: 13px;
  font-family: 'Courier New', monospace;

  &:focus {
    outline: none;
    border-color: rgba(0, 212, 255, 0.6);
  }

  option {
    background: #0a1929;
    color: #ffffff;
  }
`;

const DataTable = styled.div`
  border: 1px solid rgba(0, 212, 255, 0.3);
  background: rgba(0, 30, 60, 0.3);
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
`;

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: 50px 1fr 120px 100px 120px 120px 140px;
`;

const TableHeaderRow = styled.div`
  display: contents;

  div {
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 164, 255, 0.05) 100%);
    padding: 12px 15px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #ffffff;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    border-bottom: 1px solid rgba(0, 212, 255, 0.2);
    border-right: 1px solid rgba(0, 212, 255, 0.1);

    &:last-child {
      border-right: none;
    }
  }
`;

const TableDataRow = styled.div`
  display: contents;

  div {
    padding: 12px 15px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #ffffff;
    font-weight: 600;
    border-bottom: 1px solid rgba(0, 212, 255, 0.1);
    border-right: 1px solid rgba(0, 212, 255, 0.05);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:last-child {
      border-right: none;
    }
  }

  &:hover div {
    background: rgba(0, 212, 255, 0.05);
  }

  &:last-child div {
    border-bottom: none;
  }
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;

  ${props => {
    switch (props.$status) {
      case 'active':
        return 'background: rgba(0, 255, 136, 0.2); color: #00ff88; border: 1px solid rgba(0, 255, 136, 0.4);';
      case 'trial':
        return 'background: rgba(0, 212, 255, 0.2); color: #00d4ff; border: 1px solid rgba(0, 212, 255, 0.4);';
      case 'expired':
        return 'background: rgba(255, 100, 100, 0.2); color: #ff6666; border: 1px solid rgba(255, 100, 100, 0.4);';
      case 'canceled':
        return 'background: rgba(136, 136, 136, 0.2); color: #888888; border: 1px solid rgba(136, 136, 136, 0.4);';
      case 'past_due':
        return 'background: rgba(255, 165, 0, 0.2); color: #ffa500; border: 1px solid rgba(255, 165, 0, 0.4);';
      default:
        return 'background: rgba(136, 136, 136, 0.2); color: #888888; border: 1px solid rgba(136, 136, 136, 0.4);';
    }
  }}
`;

const SmallButton = styled.button`
  background: rgba(0, 30, 60, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.4);
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 10px;
  font-weight: 700;
  padding: 5px 10px;
  cursor: pointer;
  margin-right: 5px;
  border-radius: 3px;
  text-transform: uppercase;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
  }
`;

const DeleteButton = styled(SmallButton)`
  border-color: rgba(255, 100, 100, 0.5);

  &:hover {
    background: rgba(255, 100, 100, 0.2);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  margin-bottom: 15px;
  align-items: center;
`;

const FormLabel = styled.label`
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #ffffff;
  font-weight: 700;
  text-transform: uppercase;
`;

const FormInput = styled.input`
  background: rgba(0, 30, 60, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  padding: 12px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  font-weight: 600;
  width: 100%;

  &:focus {
    outline: none;
    border-color: rgba(0, 212, 255, 0.6);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormSelect = styled.select`
  background: rgba(0, 30, 60, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  padding: 12px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  font-weight: 600;
  width: 100%;

  &:focus {
    outline: none;
    border-color: rgba(0, 212, 255, 0.6);
  }

  option {
    background: #0a1929;
    color: #ffffff;
  }
`;

const Message = styled.div<{ $type: 'success' | 'error' | 'warning' }>`
  padding: 12px 15px;
  border-radius: 6px;
  margin-bottom: 15px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${props => {
    switch (props.$type) {
      case 'success':
        return 'background: rgba(0, 255, 136, 0.1); color: #00ff88; border: 1px solid rgba(0, 255, 136, 0.3);';
      case 'warning':
        return 'background: rgba(255, 165, 0, 0.1); color: #ffa500; border: 1px solid rgba(255, 165, 0, 0.3);';
      default:
        return 'background: rgba(255, 100, 100, 0.1); color: #ff6666; border: 1px solid rgba(255, 100, 100, 0.3);';
    }
  }}
`;

const CloseButton = styled.span`
  cursor: pointer;
  font-size: 16px;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

const NoData = styled.div`
  padding: 40px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Courier New', monospace;
  font-size: 13px;
`;

const ModalContent = styled.div`
  padding: 20px;
`;

interface Field {
  id: string;
  name: string;
  email: string;
}

const SubscriptionManagement: React.FC = () => {
  const { confirm, modalProps } = useConfirmModal();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [stats, setStats] = useState<SubscriptionStats | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [fields, setFields] = useState<Field[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Modals
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [showGrantAllModal, setShowGrantAllModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);

  // Form state
  const [grantForm, setGrantForm] = useState({
    fieldId: '',
    planId: '',
    durationDays: 30,
  });
  const [grantAllForm, setGrantAllForm] = useState({
    planId: '',
    durationDays: 30,
  });
  const [extendDays, setExtendDays] = useState(30);

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [subsData, statsData, plansData] = await Promise.all([
        getSubscriptions(statusFilter || undefined, 'field'),
        getSubscriptionStats(),
        getPlans('field'),
      ]);
      setSubscriptions(subsData);
      setStats(statsData);
      setPlans(plansData);

      // Load fields for dropdown
      try {
        const response = await axios.get(`${API_URL}/api/field/registered`);
        setFields(response.data || []);
      } catch {
        setFields([]);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  };

  const handleGrant = async () => {
    if (!grantForm.fieldId || !grantForm.planId) {
      setMessage({ type: 'error', text: 'Please select a field and plan' });
      return;
    }

    try {
      const result = await grantSubscription(grantForm);
      setMessage({ type: 'success', text: result.message });
      setShowGrantModal(false);
      setGrantForm({ fieldId: '', planId: '', durationDays: 30 });
      loadData();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.message });
    }
  };

  const handleGrantAll = async () => {
    if (!grantAllForm.planId) {
      setMessage({ type: 'error', text: 'Please select a plan' });
      return;
    }

    try {
      const result = await grantSubscriptionToAll(grantAllForm);
      setMessage({
        type: 'success',
        text: `${result.message}. Created: ${result.created}, Skipped: ${result.skipped}`
      });
      setShowGrantAllModal(false);
      setGrantAllForm({ planId: '', durationDays: 30 });
      loadData();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.message });
    }
  };

  const handleExtend = async () => {
    if (!selectedSubscription) return;

    try {
      const result = await extendSubscription(selectedSubscription.id, extendDays);
      setMessage({ type: 'success', text: result.message });
      setShowExtendModal(false);
      setSelectedSubscription(null);
      setExtendDays(30);
      loadData();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.message });
    }
  };

  const handleCancel = async (subscription: Subscription) => {
    const confirmed = await confirm({
      title: 'Cancel Subscription',
      message: 'Are you sure you want to cancel this subscription?',
      confirmText: 'Cancel Subscription',
      cancelText: 'Keep',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      const result = await cancelSubscription(subscription.id);
      setMessage({ type: 'success', text: result.message });
      loadData();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.message });
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getPlanName = (planId: string) => {
    const plan = plans.find(p => p.id === planId);
    return plan?.displayName || plan?.name || planId.slice(0, 8);
  };

  const getFieldName = (subscriberId: string) => {
    const field = fields.find(f => f.id === subscriberId);
    return field?.name || subscriberId.slice(0, 12) + '...';
  };

  if (loading && subscriptions.length === 0) {
    return <Container>Loading...</Container>;
  }

  return (
    <Container>
      <PageHeader>
        <PageTitle>Field Subscriptions</PageTitle>
      </PageHeader>

      {message && (
        <Message $type={message.type}>
          {message.text}
          <CloseButton onClick={() => setMessage(null)}>×</CloseButton>
        </Message>
      )}

      {/* Stats */}
      {stats && (
        <StatsGrid>
          <StatCard>
            <StatValue>{stats.trialCount}</StatValue>
            <StatLabel>Trial</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.activeCount}</StatValue>
            <StatLabel>Active</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.expiredCount}</StatValue>
            <StatLabel>Expired</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.canceledCount}</StatValue>
            <StatLabel>Canceled</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.totalRevenueFormatted}</StatValue>
            <StatLabel>Revenue</StatLabel>
          </StatCard>
        </StatsGrid>
      )}

      {/* Actions */}
      <ActionsBar>
        <ActionButton $variant="success" onClick={() => setShowGrantModal(true)}>
          + Grant to Field
        </ActionButton>
        <ActionButton $variant="warning" onClick={() => setShowGrantAllModal(true)}>
          + Grant to All
        </ActionButton>
        <ActionButton onClick={loadData}>
          Refresh
        </ActionButton>
      </ActionsBar>

      {/* Filters */}
      <FilterBar>
        <FilterLabel>Status:</FilterLabel>
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All</option>
          <option value="trial">Trial</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="canceled">Canceled</option>
          <option value="past_due">Past Due</option>
        </Select>
      </FilterBar>

      {/* Subscriptions Table */}
      <DataTable>
        <TableGrid>
          <TableHeaderRow>
            <div>#</div>
            <div>Field</div>
            <div>Plan</div>
            <div>Status</div>
            <div>Start</div>
            <div>End</div>
            <div>Actions</div>
          </TableHeaderRow>
          {subscriptions.length === 0 ? (
            <div style={{ gridColumn: '1 / -1' }}>
              <NoData>No subscriptions found</NoData>
            </div>
          ) : (
            subscriptions.map((sub, index) => (
              <TableDataRow key={sub.id}>
                <div>{index + 1}</div>
                <div title={sub.subscriberId}>{getFieldName(sub.subscriberId)}</div>
                <div>{getPlanName(sub.planId)}</div>
                <div>
                  <StatusBadge $status={sub.status}>{sub.status}</StatusBadge>
                </div>
                <div>{formatDate(sub.currentPeriodStart)}</div>
                <div>{formatDate(sub.currentPeriodEnd)}</div>
                <div>
                  <SmallButton onClick={() => {
                    setSelectedSubscription(sub);
                    setShowExtendModal(true);
                  }}>
                    Extend
                  </SmallButton>
                  {sub.status !== 'canceled' && (
                    <DeleteButton onClick={() => handleCancel(sub)}>
                      Cancel
                    </DeleteButton>
                  )}
                </div>
              </TableDataRow>
            ))
          )}
        </TableGrid>
      </DataTable>

      {/* Grant Modal */}
      <Modal
        isOpen={showGrantModal}
        onClose={() => setShowGrantModal(false)}
        title="Grant Subscription to Field"
      >
        <ModalContent>
          <FormRow>
            <FormLabel>Field</FormLabel>
            <FormSelect
              value={grantForm.fieldId}
              onChange={(e) => setGrantForm({ ...grantForm, fieldId: e.target.value })}
            >
              <option value="">-- Select Field --</option>
              {fields.map(field => (
                <option key={field.id} value={field.id}>
                  {field.name} ({field.email})
                </option>
              ))}
            </FormSelect>
          </FormRow>
          <FormRow>
            <FormLabel>Plan</FormLabel>
            <FormSelect
              value={grantForm.planId}
              onChange={(e) => setGrantForm({ ...grantForm, planId: e.target.value })}
            >
              <option value="">-- Select Plan --</option>
              {plans.filter(p => p.isActive).map(plan => (
                <option key={plan.id} value={plan.id}>
                  {plan.displayName} ({plan.name})
                </option>
              ))}
            </FormSelect>
          </FormRow>
          <FormRow>
            <FormLabel>Duration (days)</FormLabel>
            <FormInput
              type="number"
              value={grantForm.durationDays}
              onChange={(e) => setGrantForm({ ...grantForm, durationDays: parseInt(e.target.value) || 30 })}
              min="1"
              max="365"
            />
          </FormRow>
          <ActionButton $variant="success" onClick={handleGrant} style={{ width: '100%', marginTop: 10 }}>
            Grant Subscription
          </ActionButton>
        </ModalContent>
      </Modal>

      {/* Grant All Modal */}
      <Modal
        isOpen={showGrantAllModal}
        onClose={() => setShowGrantAllModal(false)}
        title="Grant Subscription to All Fields"
      >
        <ModalContent>
          <Message $type="warning">
            This will grant subscription to ALL fields that don't have one yet!
            <span></span>
          </Message>
          <FormRow>
            <FormLabel>Plan</FormLabel>
            <FormSelect
              value={grantAllForm.planId}
              onChange={(e) => setGrantAllForm({ ...grantAllForm, planId: e.target.value })}
            >
              <option value="">-- Select Plan --</option>
              {plans.filter(p => p.isActive).map(plan => (
                <option key={plan.id} value={plan.id}>
                  {plan.displayName} ({plan.name})
                </option>
              ))}
            </FormSelect>
          </FormRow>
          <FormRow>
            <FormLabel>Duration (days)</FormLabel>
            <FormInput
              type="number"
              value={grantAllForm.durationDays}
              onChange={(e) => setGrantAllForm({ ...grantAllForm, durationDays: parseInt(e.target.value) || 30 })}
              min="1"
              max="365"
            />
          </FormRow>
          <ActionButton $variant="warning" onClick={handleGrantAll} style={{ width: '100%', marginTop: 10 }}>
            Grant to All Fields
          </ActionButton>
        </ModalContent>
      </Modal>

      {/* Extend Modal */}
      <Modal
        isOpen={showExtendModal}
        onClose={() => {
          setShowExtendModal(false);
          setSelectedSubscription(null);
        }}
        title="Extend Subscription"
      >
        <ModalContent>
          {selectedSubscription && (
            <>
              <FormRow>
                <FormLabel>Field</FormLabel>
                <FormInput type="text" value={getFieldName(selectedSubscription.subscriberId)} disabled />
              </FormRow>
              <FormRow>
                <FormLabel>Current End</FormLabel>
                <FormInput type="text" value={formatDate(selectedSubscription.currentPeriodEnd)} disabled />
              </FormRow>
              <FormRow>
                <FormLabel>Extend by (days)</FormLabel>
                <FormInput
                  type="number"
                  value={extendDays}
                  onChange={(e) => setExtendDays(parseInt(e.target.value) || 30)}
                  min="1"
                  max="365"
                />
              </FormRow>
              <ActionButton $variant="success" onClick={handleExtend} style={{ width: '100%', marginTop: 10 }}>
                Extend Subscription
              </ActionButton>
            </>
          )}
        </ModalContent>
      </Modal>

      <ConfirmModal {...modalProps} />
    </Container>
  );
};

export default SubscriptionManagement;
